import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo, useCallback } from 'react';
import { IOption } from 'shared/components/Dropdown';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ApolloError, useApolloClient } from '@apollo/client';
import { STRIPE_ERROR_CODE_MAP } from 'shared/components/SidePanel/components/InvoicingPanel/utils/constants';
import { captureException } from '@sentry/react';
import { DiscoverResult, ErrorResponse, ExposedError, Reader, Terminal } from '@stripe/terminal-js';
import { GetTerminalTokenDocument, PaymentMedium as PaymentMedium, PaymentRelationship } from 'shared/types/graphql';
import { StripeError } from '@stripe/stripe-js';
import { StripeTerminalClinicPetParent } from './types/StripeTerminalClinicPetParent';
import { GraphQLFetchPolicies } from 'shared/enums';

export enum InvoiceStatus {
  // Terminal Invoice Statuses
  Pending,
  Canceled,
  Received,

  // Credit Card Statuses
  CardProcessing,
  CardSuccessful,

  // Universal Statuses
  Error,
  Default,
  NoTerminalsFound,

  TerminalProcessRetry,
  Sending,
}

const paymentMediumOptions: IOption[] = [
  {
    text: 'Payment Terminal',
    value: PaymentMedium.StripeTerminal,
  },
  {
    text: 'Credit or Debit Card',
    value: PaymentMedium.StripeVirtualTerminal,
  },
  {
    text: 'Digital Invoice in Chat',
    value: PaymentMedium.StripeOnline,
  },
];

interface IStripeTerminalProvider {
  getErrorMessage: (error: Error | ApolloError | ExposedError | StripeError, message?: string | undefined) => string;
  terminal?: Terminal;
  terminalOptions: IOption[] | undefined;
  isInitialized: boolean;
  disconnectReader: () => Promise<void>;
  connectReader: (selectedReader: Reader) => Promise<(ErrorResponse & { reader: Reader }) | undefined | null>;
  createTerminal: () => void;
  resetTerminalList: () => void;
  currentInvoiceStatus: InvoiceStatus;
  setCurrentInvoiceStatus: (status: InvoiceStatus) => void;
  selectedPetParent?: StripeTerminalClinicPetParent;
  setSelectedPetParent: (parent?: StripeTerminalClinicPetParent) => void;
  suggestedPetParentOptions?: StripeTerminalClinicPetParent[];
  setSuggestedPetParentOptions: (parents: StripeTerminalClinicPetParent[]) => void;
  currentPaymentMedium: PaymentMedium;
  setCurrentPaymentMedium: (medium: PaymentMedium) => void;
  paymentMediumOptions: IOption[];
  setAmountToInvoice: (amount: string) => void;
  amountToInvoice: string;
  setInvoiceIds: (ids: string[] | null) => void;
  invoiceIds: string[] | null;
  setPaymentRelationship: (paymentRelationship: PaymentRelationship | null) => void;
  paymentRelationship: PaymentRelationship | null;
  setInvoiceIdentifier: (identifier: string | null) => void;
  invoiceIdentifier: string | null;
  setIsBalancePreSelected: (isBalancePreSelected: boolean) => void;
  isBalancePreSelected: boolean;
}

export enum ConnectionStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  NOT_CONNECTED = 'not_connected',
}

const getMessageCode = (error: ExposedError | StripeError): string => {
  if (error.decline_code) return STRIPE_ERROR_CODE_MAP[error.decline_code];
  else if (error.code) return STRIPE_ERROR_CODE_MAP[error.code];
  else return STRIPE_ERROR_CODE_MAP['default'];
};

// Make sure to provide an error message in the function call if it is not a Stripe error (ExposedError)
const getErrorMessage = (error: Error | ApolloError | ExposedError | StripeError, message?: string): string => {
  console.error(error);
  if (!message) {
    return getMessageCode(error);
  } else {
    return message;
  }
};

const StripeTerminalContext = createContext<IStripeTerminalProvider>({
  terminal: undefined,
  getErrorMessage,
  terminalOptions: [],
  isInitialized: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  disconnectReader: (): Promise<void> => {
    throw new Error('disconnectReader(): void not implemented');
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  connectReader: (selectedReader: Reader): Promise<(ErrorResponse & { reader: Reader }) | undefined | null> => {
    throw new Error('connectReader(): void not implemented');
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createTerminal: (): void => {
    throw new Error('createTerminal(): void not implemented');
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  resetTerminalList: (): void => {
    throw new Error('resetTerminalList(): void not implemented');
  },
  currentInvoiceStatus: InvoiceStatus.Default,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setCurrentInvoiceStatus: (status: InvoiceStatus): void => {
    throw new Error('setCurrentInvoiceStatus(): void not implemented');
  },
  selectedPetParent: undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSelectedPetParent: (parent?: StripeTerminalClinicPetParent): void => {
    throw new Error('setSelectedPetParent(): void not implemented');
  },
  suggestedPetParentOptions: [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSuggestedPetParentOptions: (parents: StripeTerminalClinicPetParent[]): void => {
    throw new Error('setSuggestedPetParentOptions(): void not implemented');
  },
  currentPaymentMedium: PaymentMedium.StripeVirtualTerminal,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setCurrentPaymentMedium: (medium: PaymentMedium): void => {
    throw new Error('setCurrentPaymentMedium(): void not implemented');
  },
  paymentMediumOptions,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setAmountToInvoice: (amount: string): void => {
    throw new Error('setAmountToInvoice(): void not implemented');
  },
  amountToInvoice: '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setInvoiceIds: (ids: string[] | null): void => {
    throw new Error('setInvoiceIds(): void not implemented');
  },
  invoiceIds: null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setPaymentRelationship: (paymentRelationship: PaymentRelationship | null): void => {
    throw new Error('setPaymentRelationship(): void not implemented');
  },
  paymentRelationship: null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setInvoiceIdentifier: (identifier: string | null): void => {
    throw new Error('setInvoiceIdentifier(): void not implemented');
  },
  invoiceIdentifier: null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsBalancePreSelected: (isBalancePreSelected: boolean): void => {
    throw new Error('setIsBalancePreSelected(): void not implemented');
  },
  isBalancePreSelected: false,
});

interface StripeTerminalProviderProps {
  children: ReactNode;
}

export const useStripeTerminalProvider = (): IStripeTerminalProvider =>
  useContext<IStripeTerminalProvider>(StripeTerminalContext);

const StripeTerminalProvider = ({ children }: StripeTerminalProviderProps): JSX.Element => {
  const { currentClinic, currentClinicId } = useClinicUser();
  const apolloClient = useApolloClient();
  const [terminalOptions, setTerminalOptions] = useState<IOption[] | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentInvoiceStatus, setCurrentInvoiceStatus] = useState<InvoiceStatus>(InvoiceStatus.Default);
  const [selectedPetParent, setSelectedPetParent] = useState<StripeTerminalClinicPetParent>();
  const [suggestedPetParentOptions, setSuggestedPetParentOptions] = useState<StripeTerminalClinicPetParent[]>([]);
  const [currentPaymentMedium, setCurrentPaymentMedium] = useState<PaymentMedium>(PaymentMedium.StripeVirtualTerminal);
  const [amountToInvoice, setAmountToInvoice] = useState<string>('');
  const [invoiceIds, setInvoiceIds] = useState<string[] | null>(null);
  const [paymentRelationship, setPaymentRelationship] = useState<PaymentRelationship | null>(null);
  const [invoiceIdentifier, setInvoiceIdentifier] = useState<string | null>(null);
  const [isBalancePreSelected, setIsBalancePreSelected] = useState(false);

  const [terminal, setTerminal] = useState<Terminal>();
  const location = useMemo(
    () => currentClinic?.stripeTerminalLocations?.find((location) => location.isActive),
    [currentClinic],
  );

  const fetchConnectionToken = useCallback(async () => {
    if (!location) {
      return '';
    }
    try {
      const result = await apolloClient.query({
        query: GetTerminalTokenDocument,
        variables: {
          where: {
            locationId: location.stripeId,
          },
        },
        fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
      });

      return result?.data?.getTerminalToken?.secret || '';
    } catch (e) {
      console.error(e);
      captureException(e);
      return '';
    }
  }, [location, apolloClient]);

  const createTerminal = useCallback(async () => {
    const { loadStripeTerminal } = await import('@stripe/terminal-js');
    const StripeTerminal = await loadStripeTerminal();
    setIsInitialized(false);
    if (!location || !currentClinicId) {
      setIsInitialized(true);
      return;
    }

    if (StripeTerminal?.create) {
      const newTerminal = StripeTerminal.create({
        onFetchConnectionToken: fetchConnectionToken,
        onUnexpectedReaderDisconnect: (evt) => {
          setCurrentInvoiceStatus(InvoiceStatus.Default);
          console.error(evt.error || new Error('Unexpected reader disconnect'), 'Unexpected reader disconnect');
        },
        onConnectionStatusChange: (evt) => console.info('Connection status changed:', { evt }),
        onPaymentStatusChange: (evt) => console.info('Payment status changed:', { evt }),
      });
      setTerminal(newTerminal);
    }
  }, [currentClinicId, location, fetchConnectionToken]);

  const connectReader = useCallback(
    async (
      selectedReader: Reader,
    ): Promise<
      | (ErrorResponse & {
          reader: Reader;
        })
      | null
      | undefined
    > => {
      try {
        if (!terminal) return;
        const status = terminal?.getConnectionStatus();

        if (status === ConnectionStatus.CONNECTED || status === ConnectionStatus.CONNECTING) return;

        const reader = (await terminal.connectReader(selectedReader)) as ErrorResponse & {
          reader: Reader;
        };
        return reader;
      } catch (e) {
        captureException(e);
        return null;
      }
    },
    [terminal],
  );

  const initializeReaders = useCallback(async () => {
    if (isInitialized) return;
    try {
      if (terminal?.discoverReaders) {
        const readers = (await terminal.discoverReaders()) as DiscoverResult & ErrorResponse;
        if (readers.error) {
          throw readers.error;
        } else if (readers.discoveredReaders.length === 0) {
          console.error('Unable to detect terminal readers.');
        }
        setTerminalOptions(
          readers?.discoveredReaders.map((reader) => ({
            text: reader.label || 'unknown',
            value: reader,
            isDisabled: reader.status !== 'online',
          })),
        );
        setIsInitialized(true);
      }
    } catch (e) {
      // TODO: figure out if we can prevent this error on log in
      // skip expected errors when query returns undefined while client is getting ready
      if (e?.error?.code !== 'failed_fetch_connection_token') {
        captureException(e);
      }
      console.error(e);
      setIsInitialized(true);
    }
  }, [terminal, isInitialized]);

  const disconnectReader = useCallback(async () => {
    try {
      const status = terminal?.getConnectionStatus();
      await terminal?.clearCachedCredentials();

      if (status === ConnectionStatus.CONNECTED) {
        await terminal?.disconnectReader();
      }
    } catch (e) {
      captureException(e);
      console.error(e);
    } finally {
      setIsInitialized(false);
      createTerminal();
    }
  }, [terminal, createTerminal]);

  useEffect(() => {
    if (currentClinicId) {
      createTerminal();
    }
  }, [currentClinicId, createTerminal]);

  // Initialize terminal readers
  useEffect(() => {
    initializeReaders();
  }, [initializeReaders, terminal]);

  const getTerminal = useCallback((): Terminal | undefined => {
    if (terminal) {
      return terminal;
    }
    return undefined;
  }, [terminal]);

  const resetTerminalList = useCallback(() => {
    setIsInitialized(false);
  }, []);

  const handleSetInvoiceStatus = useCallback((status: InvoiceStatus) => {
    setCurrentInvoiceStatus(status);
  }, []);

  const handleSetSelectedPetParent = useCallback((parent?: StripeTerminalClinicPetParent) => {
    setSelectedPetParent(parent);
  }, []);

  const handleSetSuggestedPetParentOptions = useCallback((parents: StripeTerminalClinicPetParent[]) => {
    setSuggestedPetParentOptions(parents);
  }, []);

  const handleSetCurrentPaymentMedium = useCallback((medium: PaymentMedium) => {
    setCurrentPaymentMedium(medium);
  }, []);

  const handleSetInvoiceAmount = useCallback((amount: string) => {
    setAmountToInvoice(amount);
  }, []);

  const handleSetInvoiceIds = useCallback((ids: string[] | null) => {
    setInvoiceIds(ids || null);
  }, []);

  const handleSetPaymentRelationship = useCallback((paymentRelationship: PaymentRelationship | null) => {
    setPaymentRelationship(paymentRelationship || null);
  }, []);

  const handleSetInvoiceIdentifier = useCallback((identifier: string | null) => {
    setInvoiceIdentifier(identifier || null);
  }, []);

  const handleSetIsBalancePreSelected = useCallback((isPreSelected: boolean) => {
    setIsBalancePreSelected(isPreSelected);
  }, []);

  return (
    <StripeTerminalContext.Provider
      value={{
        terminal: getTerminal(),
        terminalOptions,
        getErrorMessage,
        isInitialized,
        connectReader,
        disconnectReader,
        createTerminal,
        resetTerminalList,
        currentInvoiceStatus,
        selectedPetParent,
        setCurrentInvoiceStatus: handleSetInvoiceStatus,
        setSelectedPetParent: handleSetSelectedPetParent,
        suggestedPetParentOptions,
        setSuggestedPetParentOptions: handleSetSuggestedPetParentOptions,
        currentPaymentMedium,
        setCurrentPaymentMedium: handleSetCurrentPaymentMedium,
        paymentMediumOptions,
        setAmountToInvoice: handleSetInvoiceAmount,
        amountToInvoice,
        setInvoiceIds: handleSetInvoiceIds,
        invoiceIds,
        setPaymentRelationship: handleSetPaymentRelationship,
        paymentRelationship,
        setInvoiceIdentifier: handleSetInvoiceIdentifier,
        invoiceIdentifier,
        setIsBalancePreSelected: handleSetIsBalancePreSelected,
        isBalancePreSelected,
      }}
    >
      {children}
    </StripeTerminalContext.Provider>
  );
};

export default StripeTerminalProvider;
